import type { PlayEventRound } from '../types/playEvent';
import { resolveRoundCourtTiming } from './playEventMatchDisplay';
import { resolveRoundStartTime, type PlayEventRoundTimingContext } from './playEventVisibility';
import { roundHasCompletedMatches } from './playEventWithdrawal';

export type PlayEventRoundScheduleContext = PlayEventRoundTimingContext;

type RoundDrawInput = Pick<PlayEventRound, 'status' | 'startsAt' | 'roundNumber'> | null | undefined;

/** Operator may manually edit pairings for upcoming rounds (not on court or finished). */
export function canEditPlayEventRoundDraw(
  roundStatus: PlayEventRound['status'],
  hasCompletedMatches: boolean
): boolean {
  if (roundStatus === 'completed' || roundStatus === 'in_progress') {
    return false;
  }
  if (hasCompletedMatches) {
    return false;
  }
  return true;
}

function isRoundScheduleEditable(
  round: RoundDrawInput,
  roundNumber: number,
  event: PlayEventRoundScheduleContext | null | undefined,
  now: Date
): boolean {
  if (!event) {
    return true;
  }

  const roundStart = resolveRoundStartTime(
    round ?? { roundNumber, startsAt: null },
    roundNumber,
    event
  );
  if (!roundStart) {
    return true;
  }

  return resolveRoundCourtTiming(now, roundStart, event.roundDurationMinutes) === 'upcoming';
}

export function isPlayEventRoundEditable(
  round: RoundDrawInput,
  matches: Array<{ roundNumber: number; result: string }>,
  roundNumber: number,
  event?: PlayEventRoundScheduleContext | null,
  now: Date = new Date()
): boolean {
  const roundStatus = round?.status ?? 'pending';
  if (!canEditPlayEventRoundDraw(roundStatus, roundHasCompletedMatches(matches, roundNumber))) {
    return false;
  }

  return isRoundScheduleEditable(round, roundNumber, event, now);
}

export function getPlayEventRoundDrawStatusLabel(
  round: RoundDrawInput,
  matches: Array<{ roundNumber: number; result: string }>,
  roundNumber: number,
  event?: PlayEventRoundScheduleContext | null,
  now: Date = new Date()
): string {
  if (round?.status === 'completed' || roundHasCompletedMatches(matches, roundNumber)) {
    return 'Complete';
  }

  if (round?.status === 'in_progress') {
    return 'On court';
  }

  if (event) {
    const roundStart = resolveRoundStartTime(
      round ?? { roundNumber, startsAt: null },
      roundNumber,
      event
    );
    if (roundStart) {
      const timing = resolveRoundCourtTiming(now, roundStart, event.roundDurationMinutes);
      if (timing === 'past') {
        return 'Past';
      }
      if (timing === 'on_court') {
        return 'On court';
      }
    }
  }

  return isPlayEventRoundEditable(round, matches, roundNumber, event, now)
    ? 'Editable'
    : 'Scheduled';
}
